import { useLocale, useTranslation } from '@multaqa/i18n'
import { Badge } from '@multaqa/ui/components/ui/badge'
import { Button } from '@multaqa/ui/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import { PageHeader } from '@multaqa/ui/components/ui/page-header'
import { toast } from '@multaqa/ui/components/ui/sonner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@multaqa/ui/components/ui/tabs'
import { useQuery } from '@tanstack/react-query'
import { type ReactNode, useState } from 'react'
import { AdminScreen } from '@/components/admin-screen'
import { localizedText } from '@/lib/localized-text'
import { type GroupRow, platformGroupsQuery } from '@/lib/platform'
import { useSuspendGroup } from './api'
import { GroupDefaults } from './group-defaults'
import { GroupEntitlements } from './group-detail-entitlements'
import { OverviewTab } from './group-detail-overview'
import { GroupPurgeAction } from './group-detail-purge'
import { GroupInvitations } from './group-invitations'
import { ReactivateGroupDialog } from './group-reactivate-dialog'
import { GroupSettings } from './group-settings'
import { GroupUsers } from './group-users'
import { GROUP_STATUSES } from './search'

export const GROUP_DETAIL_TABS = [
  'overview',
  'entitlements',
  'users',
  'invitations',
  'settings',
  'defaults',
] as const

export type GroupDetailTab = (typeof GROUP_DETAIL_TABS)[number]

export function GroupDetailScreen({
  groupId,
  tab = 'overview',
  onTabChange,
}: {
  groupId: string
  tab?: GroupDetailTab
  onTabChange?: (tab: GroupDetailTab) => void
}) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const groups = useQuery(platformGroupsQuery)
  const group = (groups.data ?? []).find((candidate) => candidate.id === groupId)

  if (!groups.isLoading && !groups.isError && !group) {
    return (
      <div className="bg-background min-h-full" data-testid="group-detail-not-found">
        <PageHeader
          trail={[{ label: t('rail.admin.sections.customers'), href: '/groups' }]}
          title={t('groups.detail.notFound')}
          description={t('groups.detail.subtitle')}
        />
      </div>
    )
  }

  const testId = group
    ? 'group-detail'
    : groups.isLoading
      ? 'group-detail-loading'
      : 'group-detail-error'

  return (
    <AdminScreen
      // AdminScreen's `trail` type is `string[]`; PageHeader (what it forwards
      // to) accepts a linked crumb too, and the linked form is what this
      // screen has always shown.
      trail={
        [{ label: t('rail.admin.sections.customers'), href: '/groups' }] as unknown as string[]
      }
      title={group ? localizedText(group.name, locale) : groupId}
      description={t('groups.detail.subtitle')}
      actions={group ? <DetailActions group={group} /> : undefined}
      loading={groups.isLoading}
      error={groups.error}
      onRetry={() => void groups.refetch()}
      testId={testId}
    >
      {group ? (
        <Tabs
          value={tab}
          onValueChange={(value) => {
            if (isGroupDetailTab(value)) onTabChange?.(value)
          }}
          appearance="underline"
          className="min-h-0 flex-1 gap-0"
          data-testid="group-detail-tabs"
        >
          <TabsList className="overflow-x-auto px-4">
            {GROUP_DETAIL_TABS.map((value) => (
              <TabsTrigger key={value} value={value} data-testid={`group-detail-tab-${value}`}>
                {t(`groups.tabs.${value}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <LazyTab value="overview" active={tab === 'overview'}>
              <OverviewTab group={group} />
            </LazyTab>
            <LazyTab value="entitlements" active={tab === 'entitlements'}>
              <GroupEntitlements group={group} />
            </LazyTab>
            <LazyTab value="users" active={tab === 'users'}>
              <GroupUsers groupId={groupId} />
            </LazyTab>
            <LazyTab value="invitations" active={tab === 'invitations'}>
              <GroupInvitations groupId={groupId} />
            </LazyTab>
            <LazyTab value="settings" active={tab === 'settings'}>
              <GroupSettings group={group} />
            </LazyTab>
            <LazyTab value="defaults" active={tab === 'defaults'}>
              <GroupDefaults groupId={groupId} />
            </LazyTab>
          </div>
        </Tabs>
      ) : null}
    </AdminScreen>
  )
}

function isGroupDetailTab(value: string): value is GroupDetailTab {
  return GROUP_DETAIL_TABS.includes(value as GroupDetailTab)
}

function LazyTab({
  value,
  active,
  children,
}: {
  value: GroupDetailTab
  active: boolean
  children: ReactNode
}) {
  return <TabsContent value={value}>{active ? children : null}</TabsContent>
}

function DetailActions({ group }: { group: GroupRow }) {
  const { t } = useTranslation()
  const [suspending, setSuspending] = useState(false)
  const [reactivating, setReactivating] = useState(false)
  const known = (GROUP_STATUSES as readonly string[]).includes(group.status)
  // An unknown status would render a badge with no translation; active is the
  // least alarming reading and the API documents exactly these four.
  const status = known ? group.status : 'active'

  return (
    <div className="flex items-center gap-2">
      {group.status === 'active' ? (
        <Button variant="outline" size="sm" onClick={() => setSuspending(true)}>
          {t('groups.suspend')}
        </Button>
      ) : null}
      {group.status === 'suspended' || group.status === 'pending_purge' ? (
        <Button
          size="sm"
          onClick={() => setReactivating(true)}
          data-testid="group-detail-reactivate"
        >
          {t('groups.reactivate')}
        </Button>
      ) : null}
      <GroupPurgeAction group={group} />
      <Badge tone={status === 'active' ? 'success' : status === 'purged' ? 'danger' : 'warning'}>
        {t(`groups.status.${status}`)}
      </Badge>
      {suspending ? <SuspendConfirm group={group} onClose={() => setSuspending(false)} /> : null}
      {reactivating ? (
        <ReactivateGroupDialog group={group} onClose={() => setReactivating(false)} />
      ) : null}
    </div>
  )
}

function SuspendConfirm({ group, onClose }: { group: GroupRow; onClose: () => void }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const suspend = useSuspendGroup()
  const name = localizedText(group.name, locale)

  return (
    <Dialog open onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent closeLabel={t('action.cancel')} data-testid="group-detail-suspend-dialog">
        <DialogHeader>
          <DialogTitle>{t('groups.suspendDialog.title', { name })}</DialogTitle>
          <DialogDescription>{t('groups.suspendDialog.description', { name })}</DialogDescription>
        </DialogHeader>
        <DialogFooter showCloseButton={false} closeLabel={t('action.cancel')}>
          <Button variant="outline" onClick={onClose} disabled={suspend.isPending}>
            {t('action.cancel')}
          </Button>
          <Button
            variant="destructive"
            loading={suspend.isPending}
            disabled={suspend.isPending}
            onClick={() =>
              suspend.mutate(group.id, {
                onSuccess: () => {
                  toast.success(t('groups.suspendDialog.success', { name }))
                  onClose()
                },
              })
            }
            data-testid="group-detail-suspend-confirm"
          >
            {t('groups.suspendDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
